import { Check, Edit2, AlertCircle, Loader2 } from 'lucide-react';
import Button from '../Button';
import {
  GOVERNORATE_LABELS,
  EDUCATION_LEVEL_LABELS,
  DOCUMENT_SLOTS,
} from './constants';
import type { ScholarshipFormData, ScholarshipDocumentsState } from './types';
import { formatFileSize } from './validation';

interface StepReviewProps {
  values: ScholarshipFormData;
  documents: ScholarshipDocumentsState;
  onEdit: (step: number) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | null;
}

export default function StepReview({
  values,
  documents,
  onEdit,
  onSubmit,
  isSubmitting,
  submitError,
}: StepReviewProps) {
  const isSchool =
    values.education_level === 'high_school_student' ||
    values.education_level === 'high_school_graduate';

  const isUniversity =
    values.education_level === 'university_student' ||
    values.education_level === 'university_graduate';

  const governorateLabel =
    GOVERNORATE_LABELS[values.governorate] || values.governorate;

  const educationLevelLabel = values.education_level
    ? EDUCATION_LEVEL_LABELS[values.education_level] || values.education_level
    : '';

  return (
    <div className="space-y-6">
      {/* Title & Subtitle */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C] mb-2">
          راجع طلبك
        </h2>
        <p className="text-sm text-[#707070] leading-relaxed">
          راجع بياناتك ومستنداتك كويس قبل ما تبعت الطلب.
        </p>
      </div>

      <div className="space-y-4">
        {/* Card 1: Personal Info */}
        <div className="rounded-2xl border-2 border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
            <h3 className="font-bold text-[#2C2C2C] text-base">
              البيانات الشخصية
            </h3>
            <button
              type="button"
              onClick={() => onEdit(1)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#5B3FD0] hover:text-[#4729b0] transition-colors"
            >
              <Edit2 className="w-3.5 h-3.5" />
              <span>تعديل</span>
            </button>
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-[#707070]">الاسم بالكامل</dt>
              <dd className="font-medium text-[#2C2C2C] mt-0.5">{values.full_name}</dd>
            </div>
            <div>
              <dt className="text-xs text-[#707070]">البريد الإلكتروني</dt>
              <dd dir="ltr" className="font-medium text-[#2C2C2C] text-start mt-0.5">
                {values.email}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[#707070]">رقم الموبايل</dt>
              <dd dir="ltr" className="font-medium text-[#2C2C2C] text-start mt-0.5">
                {values.phone}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[#707070]">تاريخ الميلاد</dt>
              <dd dir="ltr" className="font-medium text-[#2C2C2C] text-start mt-0.5">
                {values.date_of_birth}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[#707070]">المحافظة</dt>
              <dd className="font-medium text-[#2C2C2C] mt-0.5">{governorateLabel}</dd>
            </div>
          </dl>
        </div>

        {/* Card 2: Education Info */}
        <div className="rounded-2xl border-2 border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
            <h3 className="font-bold text-[#2C2C2C] text-base">الدراسة</h3>
            <button
              type="button"
              onClick={() => onEdit(2)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#5B3FD0] hover:text-[#4729b0] transition-colors"
            >
              <Edit2 className="w-3.5 h-3.5" />
              <span>تعديل</span>
            </button>
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-[#707070]">المؤهل الدراسي</dt>
              <dd className="font-medium text-[#2C2C2C] mt-0.5">{educationLevelLabel}</dd>
            </div>

            {isSchool && values.school_name && (
              <div>
                <dt className="text-xs text-[#707070]">اسم المدرسة</dt>
                <dd className="font-medium text-[#2C2C2C] mt-0.5">{values.school_name}</dd>
              </div>
            )}

            {isUniversity && values.university_name && (
              <div>
                <dt className="text-xs text-[#707070]">اسم الجامعة</dt>
                <dd className="font-medium text-[#2C2C2C] mt-0.5">{values.university_name}</dd>
              </div>
            )}

            {isUniversity && values.faculty && (
              <div>
                <dt className="text-xs text-[#707070]">الكلية</dt>
                <dd className="font-medium text-[#2C2C2C] mt-0.5">{values.faculty}</dd>
              </div>
            )}

            {isUniversity && values.major && (
              <div>
                <dt className="text-xs text-[#707070]">التخصص</dt>
                <dd className="font-medium text-[#2C2C2C] mt-0.5">{values.major}</dd>
              </div>
            )}

            {values.graduation_year && (
              <div>
                <dt className="text-xs text-[#707070]">سنة التخرج</dt>
                <dd dir="ltr" className="font-medium text-[#2C2C2C] text-start mt-0.5">
                  {values.graduation_year}
                </dd>
              </div>
            )}

            {values.academic_grade && (
              <div>
                <dt className="text-xs text-[#707070]">النسبة / التقدير</dt>
                <dd className="font-medium text-[#2C2C2C] mt-0.5">{values.academic_grade}</dd>
              </div>
            )}

            <div className="sm:col-span-2">
              <dt className="text-xs text-[#707070]">التخصص اللي حابب تدرسه</dt>
              <dd className="font-medium text-[#2C2C2C] mt-0.5">{values.preferred_study_field}</dd>
            </div>

            <div className="sm:col-span-2">
              <dt className="text-xs text-[#707070]">ليه تستحق المنحة</dt>
              <dd className="font-normal text-[#2C2C2C] mt-1 whitespace-pre-wrap leading-relaxed text-xs sm:text-sm bg-gray-50/70 p-3 rounded-xl">
                {values.motivation}
              </dd>
            </div>

            {values.achievements && (
              <div className="sm:col-span-2">
                <dt className="text-xs text-[#707070]">إنجاز أو نشاط مميز</dt>
                <dd className="font-normal text-[#2C2C2C] mt-1 whitespace-pre-wrap leading-relaxed text-xs sm:text-sm bg-gray-50/70 p-3 rounded-xl">
                  {values.achievements}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Card 3: Documents */}
        <div className="rounded-2xl border-2 border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
            <h3 className="font-bold text-[#2C2C2C] text-base">المستندات المرفوعة</h3>
            <button
              type="button"
              onClick={() => onEdit(3)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#5B3FD0] hover:text-[#4729b0] transition-colors"
            >
              <Edit2 className="w-3.5 h-3.5" />
              <span>تعديل</span>
            </button>
          </div>

          <div className="space-y-2.5">
            {DOCUMENT_SLOTS.map((slotItem) => {
              const doc = documents[slotItem.slot];
              return (
                <div
                  key={slotItem.slot}
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50/70 text-xs sm:text-sm"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium text-[#2C2C2C]">{slotItem.label}</p>
                      <p className="text-[#707070] text-xs truncate max-w-[180px] sm:max-w-xs">
                        {doc.fileName || 'تم الرفع'}
                      </p>
                    </div>
                  </div>
                  {doc.fileSize && (
                    <span dir="ltr" className="text-xs text-[#707070] font-mono shrink-0">
                      {formatFileSize(doc.fileSize)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Inline Error Banner */}
      {submitError && (
        <div className="flex items-start gap-3 p-4 rounded-xl border-2 border-[#F46A6A] bg-[#F46A6A]/10 text-xs sm:text-sm text-[#2C2C2C]">
          <AlertCircle className="w-5 h-5 text-[#F46A6A] shrink-0 mt-0.5" />
          <p className="leading-relaxed font-medium">{submitError}</p>
        </div>
      )}

      {/* Final Submit Action */}
      <div className="pt-2">
        <Button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          variant="primary"
          className="w-full sm:w-auto min-w-[200px] h-12 text-base font-bold shadow-lg hover:shadow-xl disabled:opacity-60"
        >
          {isSubmitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>جاري الإرسال...</span>
            </span>
          ) : (
            'إرسال طلب المنحة'
          )}
        </Button>
      </div>
    </div>
  );
}
